import React, { useEffect, useState } from "react";
import { DialogRoot } from "components/ui/dialog";
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@chakra-ui/react";
import { Button } from "components/ui/button";
import api from "utils/apiService";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import * as XLSX from "xlsx"; // Import XLSX for Excel export
import { toast } from "react-toastify";
import { LuFileSpreadsheet } from "react-icons/lu";
import OfferFilter from "../../offers/components/OfferFilter";
import { useLocation, useNavigate } from "react-router-dom";
import { IoIosSearch } from "react-icons/io";

const statusLabels = {
  completed: "مكتمل",
  pending: "قيد الانتظار",
  rejected: "مرفوض",
};

const paymentTypeLabels = {
  bank: "تحويل بنكي",
  card: "بطاقة",
  cash: "نقدي",
  subscription: "اشتراك",
  offerUnlock: "فتح عرض",
};

const ExportSheet = () => {
  const [data, setData] = useState({ data: [], pagination: { totalDocs: 0 } });
  const [loading, setLoading] = useState(false);
  const [prepared, setPrepared] = useState(false);

  // Fetch data
  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/paymentRequests`);
      if (res.error) {
        console.error("API Error:", res.error);
        toast.error("خطأ في جلب البيانات");
      } else {
        setData({
          data: res?.data || [],
          pagination: res?.pagination || { totalDocs: 0 },
        });
        toast.success("تم جلب البيانات بنجاح");
        setPrepared(true);
      }
    } catch (error) {
      console.error("Unexpected Error:", error);
      toast.error("حدث خطأ غير متوقع");
    } finally {
      setLoading(false);
    }
  };

  // Export data as Excel
  const exportToExcel = () => {
    if (!data.data.length) {
      toast.info("لا توجد بيانات للتصدير");
      return;
    }
    // Prepare the data for the Excel sheet
    const exportData = data.data.map((item) => ({
      الاجمالي: item.amount || 0,
      الحالة: statusLabels[item.status] || item.status,
      النوع: paymentTypeLabels[item.type] || item.type,
      "تاريخ الانشاء": new Date(item.createdAt).toLocaleString("ar-EG", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      }),
      المستخدم: item.user?.name || "غير معروف",
      "رقم الهاتف": item.user?.phoneNumber || "غير معروف",
      "البريد الالكتروني": item.user?.contractorInfo?.email || "غير معروف",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "طلبات الدفع");

    XLSX.writeFile(wb, "طلبات_الدفع.xlsx");
    toast.success("تم تصدير البيانات بنجاح");
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get("search");
    setSearch(searchParam || "");
  }, [location.search]);

  const handleSearch = (e) => {
    const params = new URLSearchParams(location.search);
    if (e.target.value === "") {
      params.delete("search");
      navigate(`${location.pathname}?${params.toString()}`);
      return;
    }
    params.set("search", e.target.value);
    navigate(`${location.pathname}?${params.toString()}`);
  };
  return (
    <>
      <div className="relative flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-4 py-2 dark:border-navy-700 dark:bg-navy-800">
        <input
          className="w-full bg-gray-100 focus:outline-none dark:bg-navy-800"
          type="search"
          placeholder="ابحث برقم الهاتف ..."
          value={search}
          onChange={handleSearch}
        />
        <IoIosSearch size={30} />
      </div>
      <OfferFilter />
      <DialogRoot dir="rtl">
        <DialogTrigger asChild>
          <button
            className="my-4 rounded-md bg-blueSecondary py-2 px-4 text-white transition-all float-end hover:bg-brandLinear"
            variant="outline"
            size="sm"
          >
            تصدير إكسل
          </button>
        </DialogTrigger>
        <DialogContent className="fixed top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white text-gray-700 end-1/2 dark:bg-navy-800 dark:text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              تصدير البيانات
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            {loading ? (
              <div className="flex h-[200px] items-center justify-center rounded-xl bg-white shadow-sm dark:bg-navy-800">
                <AiOutlineLoading3Quarters className="animate-spin text-4xl text-blueSecondary dark:text-white" />
              </div>
            ) : (
              <div className="text-center">
                <div className="flex flex-col items-center justify-center gap-4">
                  {!prepared ? (
                    <button
                      className="my-4 rounded-md bg-blueSecondary py-2 px-4 text-white transition-all float-end hover:bg-brandLinear"
                      variant="outline"
                      size="sm"
                      onClick={fetchData}
                    >
                      جلب البيانات
                    </button>
                  ) : (
                    <button
                      onClick={exportToExcel}
                      className="my-4 flex flex-col items-center justify-center gap-2 rounded-md bg-green-500 py-2 px-4 text-white transition-all float-end hover:bg-green-400"
                    >
                      <LuFileSpreadsheet className="text-3xl" />
                      <span>تحميل إكسل</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button variant="outline">
                <span>إلغاء</span>
              </Button>
            </DialogActionTrigger>
          </DialogFooter>
          <DialogCloseTrigger />
        </DialogContent>
      </DialogRoot>
    </>
  );
};

export default ExportSheet;
